import React, { InputHTMLAttributes, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { nanoid } from 'nanoid'
import { ErrorDetail } from '../ErrorDetail/ErrorDetail'

const StyledInput = styled.input<Omit<Props, 'label'>>`
  width: 100%;
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  background-color: ${({ theme }) => theme.colors.surface.subdued};
  color: ${({ theme }) => theme.colors.font.regular};
  padding: 0.625em;
  border: 1px solid ${({ theme }) => theme.colors.button.secondaryBorder};
  border-radius: 4px;

  ::placeholder {
    color: ${({ theme }) => theme.colors.font.placeholder};
  }

  &:hover,
  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.focused};
  }

  &:active {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.selected};
  }

  ${(props) =>
    props.disabled &&
    css`
      border: 1px solid
        ${({ theme }) => theme.colors.system.interactive.disabled};

      &:hover,
      &:focus,
      &:active {
        border: 1px solid
          ${({ theme }) => theme.colors.system.interactive.disabled};
      }
    `}
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25em;

  > label {
    color: ${({ theme }) => theme.colors.font.regular};
    font-size: var(--small-text);
    font-weight: 700;
  }
`

const Field = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  row-gap: 0.25em;
`

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hideLabel?: boolean
  error?: React.ReactNode
}

export const Input: React.FC<Props> = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      error,
      hideLabel,
      label,
      name,
      required,
      value,
      onBlur,
      onChange,
      id,
      ...rest
    },
    ref,
  ) => {
    // This ensures a label is always matched to an input
    const inputId = useMemo(() => {
      return id ? id : `Input__input-id-${nanoid()}`
    }, [id])

    return (
      <Field>
        <InputContainer className={className}>
          <label htmlFor={inputId} hidden={hideLabel || rest.hidden}>
            {label}
          </label>
          <StyledInput
            id={inputId}
            aria-label={!hideLabel ? label : undefined}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            ref={ref}
            {...rest}
          />
        </InputContainer>
        {error ? <ErrorDetail error={error} /> : null}
      </Field>
    )
  },
)

Input.displayName = 'Input'
